import type { CommonNavContent, CommonNavLink } from '../types/commonNavigation';
import type {
  GlobalNavigationContentEntry,
  GlobalNavigationV2L2Block,
  PageEdgeNode,
} from './contentstackGraphQLResponse';

/** * Resolves the display text based on CMS priority:
 * 1. If only one link exists, prefer the row's custom display text.
 * 2. Otherwise, fall back to the linked page's title or heading.
 */
const resolveLinkTitle = (
  node: PageEdgeNode['node'],
  rowDisplayText?: string,
  isSingle?: boolean,
): string | undefined => {
  const fallback = node.title ?? node.heading;
  return isSingle && rowDisplayText?.trim() ? rowDisplayText.trim() : fallback;
};

const mapEdgesToLinks = (edges: PageEdgeNode[], rowDisplayText?: string): CommonNavLink[] => {
  const isSingle = edges.length === 1;

  return edges.reduce<CommonNavLink[]>((acc, { node }) => {
    const title = resolveLinkTitle(node, rowDisplayText, isSingle);
    const url = node.url;

    if (title && url) {
      acc.push({ title, url });
    }
    return acc;
  }, []);
};

const mapL2ToCommon = (l2: GlobalNavigationV2L2Block) => ({
  l2_display_text: l2.l2_display_text ?? '',
  l2_navigation_link: mapEdgesToLinks(l2.l2_navigation_linkConnection.edges, l2.l2_display_text),
  l3_navigation: l2.l3_navigation.map((l3) => ({
    l3_display_text: l3.l3_display_text ?? '',
    l3_navigation_link: mapEdgesToLinks(l3.l3_navigation_linkConnection.edges, l3.l3_display_text),
  })),
});

export const adaptGraphQLGlobalNavToCommonNav = (
  entry: GlobalNavigationContentEntry,
): CommonNavContent => {
  const l1_navigation = entry.l1_navigation.flatMap((block) =>
    block.department_navigationConnection.edges
      .filter(({ node }) => Boolean(node.title))
      .map(({ node }) => ({
        l1_display_text: node.l1_display_text ?? node.title,
        featured: block.featured ?? false,
        l1_navigation_link: mapEdgesToLinks(
          node.l1_navigation_linkConnection.edges,
          node.l1_display_text,
        ),
        l2_navigation: node.l2_navigation.map(mapL2ToCommon),
      })),
  );

  return { l1_navigation };
};
